import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { getModel, listCities, listModelPaymentMethods, updateModel, updateModelCampaign, uploadModelPhoto } from '../../../services/UserService';
import './ModelEdit.css';

const emptyModel = {
    id: '',
    uuid: '',
    name: '',
    modelName: '',
    slug: '',
    city: '',
    status: 'Active',
    description: '',
    image: '',
    photos: [],
    parameters: {},
    cityRates: [],
    travelRates: [],
    locationType: 'single',
    singleCity: '',
    tourSchedule: [],
    contacts: {},
    bookings: 0,
    campaign: null
};

const contactTypeOptions = [
    { value: 'whatsapp', label: 'WhatsApp', inputType: 'tel', placeholder: '+39 123 456 7890' },
    { value: 'telegram', label: 'Telegram', inputType: 'text', placeholder: '@username' },
    { value: 'viber', label: 'Viber', inputType: 'tel', placeholder: '+39 123 456 7890' },
    { value: 'email', label: 'Email', inputType: 'email', placeholder: 'name@example.com' },
    { value: 'phone', label: 'Phone', inputType: 'tel', placeholder: '+39 123 456 7890' },
    { value: 'instagram', label: 'Instagram', inputType: 'text', placeholder: '@username' },
    { value: 'signal', label: 'Signal', inputType: 'text', placeholder: '@username' },
    { value: 'website', label: 'Website', inputType: 'url', placeholder: 'https://example.com' },
    { value: 'custom', label: 'Custom', inputType: 'text', placeholder: 'Contact value' }
];

const campaignStatusOptions = [
    { value: 'auto', label: 'Auto-pay' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
];
const campaignTypeOptions = [
    { value: 1, label: '3 Days' },
    { value: 2, label: '7 Days' },
    { value: 3, label: '30 Days' },
    { value: 'trial', label: 'Trial' }
];
const defaultTrialStartsAt = () => new Date().toISOString().slice(0, 16);
const trialStartsAtInput = (value) => {
    if (!value) return defaultTrialStartsAt();
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return defaultTrialStartsAt();
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 16);
};
const normalizeCampaign = (campaign) => {
    if (!campaign || typeof campaign !== 'object') return null;
    let data = campaign.data;
    if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (error) { data = {}; }
    }
    data = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const isTrial = String(campaign.type || data.type || '').toLowerCase() === 'trial';
    return {
        ...campaign,
        type: isTrial ? 'trial' : 'paid',
        status: isTrial ? 'active' : ['auto', 'active', 'inactive'].includes(String(campaign.status || '').toLowerCase()) ? String(campaign.status).toLowerCase() : 'inactive',
        budget: isTrial ? 0 : Number(campaign.budget) || 2,
        data: isTrial ? {
            ...data,
            type: 'trial',
            trialDays: Number(data.trialDays || campaign.trialDays) || 7,
            startsAt: data.startsAt || campaign.startsAt || defaultTrialStartsAt()
        } : data
    };
};

const knownContactType = (key) => contactTypeOptions.find(option => option.value === key);
const humanizeContactType = (key) => key
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
const normalizeContactKey = (value) => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
const nextContactKey = (contacts) => {
    let key = 'custom';
    let suffix = 1;
    while (Object.prototype.hasOwnProperty.call(contacts, key)) {
        suffix += 1;
        key = `custom_${suffix}`;
    }
    return key;
};
const contactEditorState = (contacts) => Object.keys(contacts).reduce((state, key) => {
    if (!knownContactType(key) || key === 'custom') {
        state.customTypes[key] = true;
        state.customLabels[key] = key === 'custom' || /^custom_\d+$/.test(key) ? '' : humanizeContactType(key);
    }
    return state;
}, { customTypes: {}, customLabels: {} });

const normalizeRateValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^\d.-]/g, ''));
    return Number.isFinite(numericValue) ? numericValue : null;
};
const normalizeRateTable = (rates) => Array.isArray(rates)
    ? rates.map(rate => ({
        ...rate,
        incall: normalizeRateValue(rate?.incall),
        outcall: normalizeRateValue(rate?.outcall)
    }))
    : [];

const normalizeModel = (model = {}) => ({
    ...emptyModel,
    ...model,
    id: model.uuid || model.id || '',
    modelName: model.name || model.modelName || '',
    photos: Array.isArray(model.photos) ? model.photos : [],
    parameters: model.parameters && typeof model.parameters === 'object' ? model.parameters : {},
    cityRates: normalizeRateTable(model.cityRates),
    travelRates: normalizeRateTable(model.travelRates),
    locationType: model.locationType || 'single',
    singleCity: model.singleCity || model.city || '',
    tourSchedule: Array.isArray(model.tourSchedule) ? model.tourSchedule : [],
    contacts: model.contacts && typeof model.contacts === 'object' ? model.contacts : {},
    campaign: normalizeCampaign(model.campaign)
});
const ModelEdit = () => {

    const { id } = useParams();
    const [model, setModel] = useState(emptyModel);

    const [editingName, setEditingName] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [editingParams, setEditingParams] = useState(false);
    const [editingCity, setEditingCity] = useState(null);
    const [editingTravel, setEditingTravel] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [editingContacts, setEditingContacts] = useState(false);
    const [customContactTypes, setCustomContactTypes] = useState({});
    const [customContactLabels, setCustomContactLabels] = useState({});
    const [cities, setCities] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(Boolean(id));
    const [paymentMethodsError, setPaymentMethodsError] = useState('');
    const [isLoadingModel, setIsLoadingModel] = useState(Boolean(id));
    const [isSavingModel, setIsSavingModel] = useState(false);
    const [modelError, setModelError] = useState('');
    const [cityError, setCityError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [photoFile, setPhotoFile] = useState(null);
    const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
    const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);
    const [dragOverPhotoIndex, setDragOverPhotoIndex] = useState(null);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const photoFileInput = useRef(null);

    useEffect(() => {
        if (!id) {
            setModelError('No model was selected. Return to Models and choose a model to edit.');
            setIsLoadingModel(false);
            return undefined;
        }
        let isMounted = true;
        setIsLoadingModel(true);
        setModelError('');
        setSaveError('');
        setSaveMessage('');
        getModel(id).then(apiModel => {
            if (isMounted) {
                const normalizedModel = normalizeModel(apiModel);
                const editorContacts = contactEditorState(normalizedModel.contacts);
                setModel(normalizedModel);
                setCustomContactTypes(editorContacts.customTypes);
                setCustomContactLabels(editorContacts.customLabels);
            }
        }).catch(loadError => {
            if (isMounted) {
                setModelError(loadError instanceof TypeError ? 'Unable to connect to the models service.' : loadError.message);
            }
        }).finally(() => {
            if (isMounted) setIsLoadingModel(false);
        });
        return () => {
            isMounted = false;
        };
    }, [id]);

    useEffect(() => {
        if (!id) {
            setPaymentMethods([]);
            setPaymentMethodsError('');
            setIsLoadingPaymentMethods(false);
            return undefined;
        }
        let isMounted = true;
        setIsLoadingPaymentMethods(true);
        setPaymentMethodsError('');
        listModelPaymentMethods(id).then(methods => {
            if (isMounted) setPaymentMethods(Array.isArray(methods) ? methods : []);
        }).catch(loadError => {
            if (isMounted) {
                setPaymentMethodsError(loadError instanceof TypeError ? 'Unable to connect to the payment service.' : loadError.message);
            }
        }).finally(() => {
            if (isMounted) setIsLoadingPaymentMethods(false);
        });
        return () => {
            isMounted = false;
        };
    }, [id]);

    useEffect(() => {
        let isMounted = true;
        listCities().then(apiCities => {
            if (isMounted) setCities(Array.isArray(apiCities) ? apiCities : []);
        }).catch(loadError => {
            if (isMounted) setCityError(loadError instanceof TypeError ? 'Unable to load cities.' : loadError.message);
        });
        return () => {
            isMounted = false;
        };
    }, []);

    const contactsPayload = () => Object.entries(model.contacts).reduce((payload, [key, value]) => {
        const isCustom = customContactTypes[key] || !knownContactType(key);
        const requestedKey = isCustom
            ? normalizeContactKey(customContactLabels[key] || key) || key
            : key;
        let payloadKey = requestedKey;
        let suffix = 1;
        while (Object.prototype.hasOwnProperty.call(payload, payloadKey)) {
            suffix += 1;
            payloadKey = `${requestedKey}_${suffix}`;
        }
        payload[payloadKey] = value;
        return payload;
    }, {});

    const campaignPayload = () => {
        const campaign = model.campaign || {};
        if (campaign.type === 'trial') {
            return {
                type: 'trial',
                status: 'active',
                budget: 0,
                data: {
                    ...(campaign.data || {}),
                    type: 'trial',
                    trialDays: Number(campaign.data?.trialDays) || 7,
                    startsAt: campaign.data?.startsAt || defaultTrialStartsAt()
                }
            };
        }
        return {
            status: campaign.status || 'inactive',
            budget: Number(campaign.budget) || 2
        };
    };

    const saveModel = async () => {
        const modelId = id || model.id;
        if (!modelId || isSavingModel) return;
        setSaveError('');
        setSaveMessage('');
        setIsSavingModel(true);
        try {
            const savedModel = await updateModel(modelId, {
                name: model.name,
                slug: model.slug,
                city: model.singleCity,
                status: model.status,
                description: model.description,
                image: model.image,
                photos: model.photos,
                parameters: model.parameters,
                cityRates: normalizeRateTable(model.cityRates),
                travelRates: normalizeRateTable(model.travelRates),
                locationType: model.locationType,
                singleCity: model.singleCity,
                tourSchedule: model.tourSchedule,
                contacts: contactsPayload(),
                bookings: model.bookings
            });
            const savedCampaign = await updateModelCampaign(modelId, campaignPayload());
            const normalizedSavedModel = normalizeModel({
                ...savedModel,
                campaign: savedCampaign || model.campaign
            });
            const editorContacts = contactEditorState(normalizedSavedModel.contacts);
            setModel(normalizedSavedModel);
            setCustomContactTypes(editorContacts.customTypes);
            setCustomContactLabels(editorContacts.customLabels);
            setSaveMessage('Changes saved.');
        } catch (saveModelError) {
            setSaveError(saveModelError instanceof TypeError ? 'Unable to connect to the models service.' : saveModelError.message);
        } finally {
            setIsSavingModel(false);
        }
    };

    const cityOptions = cities.map(city => ({ value: city.uuid, label: city.name }));
    const optionsForValue = (value) => value && !cityOptions.some(option => option.value === value)
        ? [{ value, label: value }, ...cityOptions]
        : cityOptions;
    const durationOptions = ['30 Minutes', '1 Hour', '2 Hours', '3 Hours', '4 Hours', '6 Hours', '8 Hours', '12 Hours', '24 Hours', '48 Hours', '72 Hours'];
    const heightOptions = ['N/A', ...Array.from({length: 54}, (_, i) => (140 + i) + 'cm')];
    const weightOptions = ['N/A', ...Array.from({length: 61}, (_, i) => (40 + i) + 'kg')];

    const paramSelect = (key) => {
        if (key === 'height') return heightOptions;
        if (key === 'weight') return weightOptions;
        return null;
    };

    const handleParamChange = (key, value) => {
        setModel(prev => ({
            ...prev,
            parameters: { ...prev.parameters, [key]: value }
        }));
    };

    const updateContactValue = (key, value) => {
        setModel(prev => ({
            ...prev,
            contacts: { ...prev.contacts, [key]: value }
        }));
    };

    const addContact = () => {
        const key = nextContactKey(model.contacts);
        setModel(prev => ({
            ...prev,
            contacts: { ...prev.contacts, [key]: '' }
        }));
        setCustomContactTypes(prev => ({ ...prev, [key]: true }));
        setCustomContactLabels(prev => ({ ...prev, [key]: '' }));
    };

    const removeContact = (key) => {
        setModel(prev => {
            const { [key]: removedContact, ...contacts } = prev.contacts;
            return { ...prev, contacts };
        });
        setCustomContactTypes(prev => {
            const { [key]: removedType, ...types } = prev;
            return types;
        });
        setCustomContactLabels(prev => {
            const { [key]: removedLabel, ...labels } = prev;
            return labels;
        });
    };

    const updateCustomContactLabel = (key, value) => {
        setCustomContactLabels(prev => ({ ...prev, [key]: value }));
    };

    const updateContactType = (currentKey, nextType) => {
        if (nextType === 'custom') {
            setCustomContactTypes(prev => ({ ...prev, [currentKey]: true }));
            setCustomContactLabels(prev => ({
                ...prev,
                [currentKey]: prev[currentKey] || knownContactType(currentKey)?.label || humanizeContactType(currentKey)
            }));
            return;
        }

        if (nextType === currentKey) {
            setCustomContactTypes(prev => {
                const { [currentKey]: removedType, ...types } = prev;
                return types;
            });
            setCustomContactLabels(prev => {
                const { [currentKey]: removedLabel, ...labels } = prev;
                return labels;
            });
            return;
        }

        if (Object.prototype.hasOwnProperty.call(model.contacts, nextType)) return;
        setModel(prev => {
            const contacts = Object.entries(prev.contacts).reduce((nextContacts, [key, value]) => {
                nextContacts[key === currentKey ? nextType : key] = value;
                return nextContacts;
            }, {});
            return { ...prev, contacts };
        });
        setCustomContactTypes(prev => {
            const { [currentKey]: removedType, ...types } = prev;
            return types;
        });
        setCustomContactLabels(prev => {
            const { [currentKey]: removedLabel, ...labels } = prev;
            return labels;
        });
    };

    const handleCityRateChange = (index, field, value) => {
        setModel(prev => ({
            ...prev,
            cityRates: prev.cityRates.map((r, i) => 
                i === index ? { ...r, [field]: field === 'duration' ? value : (value === '' ? null : Number(value)) } : r
            )
        }));
    };

    const handleTravelRateChange = (index, field, value) => {
        setModel(prev => ({
            ...prev,
            travelRates: prev.travelRates.map((r, i) => 
                i === index ? { ...r, [field]: field === 'duration' ? value : (value === '' ? null : Number(value)) } : r
            )
        }));
    };

    const addCityRate = () => {
        setModel(prev => ({
            ...prev,
            cityRates: [...prev.cityRates, { duration: '', incall: null, outcall: null }]
        }));
        setEditingCity(model.cityRates.length);
    };

    const addTravelRate = () => {
        setModel(prev => ({
            ...prev,
            travelRates: [...prev.travelRates, { duration: '', incall: null, outcall: null }]
        }));
        setEditingTravel(model.travelRates.length);
    };

    const deleteCityRate = (index) => {
        setModel(prev => ({
            ...prev,
            cityRates: prev.cityRates.filter((_, i) => i !== index)
        }));
        setEditingCity(null);
    };

    const deleteTravelRate = (index) => {
        setModel(prev => ({
            ...prev,
            travelRates: prev.travelRates.filter((_, i) => i !== index)
        }));
        setEditingTravel(null);
    };

    const deletePhoto = (index) => {
        setModel(prev => ({
            ...prev,
            photos: prev.photos.filter((_, i) => i !== index)
        }));
    };

    const handlePhotoDragStart = (event, index) => {
        setDraggedPhotoIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
    };

    const handlePhotoDragOver = (event, index) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOverPhotoIndex(index);
    };

    const handlePhotoReorderDrop = (event, targetIndex) => {
        event.preventDefault();
        const sourceIndex = draggedPhotoIndex;
        if (sourceIndex === null || sourceIndex === targetIndex) {
            setDraggedPhotoIndex(null);
            setDragOverPhotoIndex(null);
            return;
        }

        setModel(prev => {
            const photos = [...prev.photos];
            const [movedPhoto] = photos.splice(sourceIndex, 1);
            photos.splice(targetIndex, 0, movedPhoto);
            return { ...prev, photos };
        });
        setDraggedPhotoIndex(null);
        setDragOverPhotoIndex(null);
        setSaveMessage('Photo order changed. Click Save Changes to persist the model profile.');
    };

    const handlePhotoDragEnd = () => {
        setDraggedPhotoIndex(null);
        setDragOverPhotoIndex(null);
    };

    const setSelectedPhotoFile = (file) => {
        setPhotoError('');
        if (!file) {
            setPhotoFile(null);
            return;
        }
        if (!file.type.startsWith('image/')) {
            setPhotoFile(null);
            setPhotoError('Choose an image file.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setPhotoFile(null);
            setPhotoError('Image must be 10 MB or smaller.');
            return;
        }
        setPhotoFile(file);
    };

    const handlePhotoFileChange = (event) => {
        setSelectedPhotoFile(event.target.files?.[0] || null);
    };

    const handlePhotoDrop = (event) => {
        event.preventDefault();
        setIsDraggingPhoto(false);
        setSelectedPhotoFile(event.dataTransfer.files?.[0] || null);
    };

    const addPhoto = async () => {
        if (!photoFile) {
            setPhotoError('Choose an image file to upload.');
            return;
        }
        const modelId = id || model.id;
        if (!modelId) {
            setPhotoError('A model must be loaded before uploading a photo.');
            return;
        }
        setIsUploadingPhoto(true);
        setPhotoError('');
        try {
            const uploadedPhotoUrl = await uploadModelPhoto(modelId, photoFile);
            setModel(prev => ({ ...prev, photos: [...prev.photos, uploadedPhotoUrl] }));
            setPhotoFile(null);
            if (photoFileInput.current) photoFileInput.current.value = '';
            setShowUpload(false);
            setSaveMessage('Photo uploaded. Click Save Changes to persist the model profile.');
        } catch (uploadError) {
            setPhotoError(uploadError instanceof TypeError ? 'Unable to connect to the photo service.' : uploadError.message);
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handleConfirmDelete = () => {
        if (confirmDelete) {
            confirmDelete.action();
            setConfirmDelete(null);
        }
    };

    const askDelete = (label, action) => {
        setConfirmDelete({ label, action });
    };

    const openPhotoUpload = () => {
        setPhotoFile(null);
        setPhotoError('');
        setIsDraggingPhoto(false);
        if (photoFileInput.current) photoFileInput.current.value = '';
        setShowUpload(true);
    };

    const closePhotoUpload = () => {
        setPhotoFile(null);
        setPhotoError('');
        setIsDraggingPhoto(false);
        if (photoFileInput.current) photoFileInput.current.value = '';
        setShowUpload(false);
    };

    return (
        <div className="model-edit">
            <NavigationMenu />
            <div className="model-edit-content">
                <header className="model-edit-header">
                    <div className="model-edit-header-row">
                        <div>
                            <h1 className="model-edit-title">Edit Model</h1>
                            <p className="model-edit-subtitle">Manage model profile, photos, rates, and availability</p>
                        </div>
                        <button className="section-btn" onClick={saveModel} disabled={isLoadingModel || isSavingModel}>
                            {isSavingModel ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                    {isLoadingModel && <p className="model-edit-feedback" role="status">Loading model...</p>}
                    {modelError && <p className="model-edit-error" role="alert">{modelError}</p>}
                    {cityError && !modelError && <p className="model-edit-feedback" role="status">{cityError} Existing city values are preserved.</p>}
                    {saveError && <p className="model-edit-error" role="alert">{saveError}</p>}
                    {saveMessage && <p className="model-edit-success" role="status">{saveMessage}</p>}
                </header>

                {isLoadingModel ? (
                    <div className="model-edit-state" role="status">Loading model...</div>
                ) : modelError ? (
                    <div className="model-edit-state model-edit-state-error" role="alert">{modelError}</div>
                ) : (
                    <fieldset className="model-edit-form" disabled={isSavingModel}>
                    <div className="model-edit-layout">
                    <div className="model-edit-left">
                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Photos</p>
                                <button className="section-btn" onClick={openPhotoUpload}>Add Photo</button>
                            </div>
                            <div className="model-photos" aria-label="Model photos">
                                {model.photos.map((photo, i) => (
                                    <div
                                        className={`model-photo${draggedPhotoIndex === i ? ' is-dragging' : ''}${dragOverPhotoIndex === i && draggedPhotoIndex !== i ? ' is-drag-over' : ''}`}
                                        key={`${photo}-${i}`}
                                        draggable
                                        onDragStart={(event) => handlePhotoDragStart(event, i)}
                                        onDragOver={(event) => handlePhotoDragOver(event, i)}
                                        onDrop={(event) => handlePhotoReorderDrop(event, i)}
                                        onDragEnd={handlePhotoDragEnd}
                                        aria-grabbed={draggedPhotoIndex === i}
                                        title="Drag to reorder photo"
                                    >
                                        <img src={photo} alt={`${model.name || 'Model'} ${i + 1}`} onClick={() => setLightbox(photo)} />
                                        <button type="button" className="photo-delete" aria-label={`Delete photo ${i + 1}`} onClick={() => askDelete('this photo', () => deletePhoto(i))}>&times;</button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Name</p>
                                <button className="section-btn" onClick={() => setEditingName(!editingName)}>
                                    {editingName ? 'Save' : 'Edit'}
                                </button>
                            </div>
                            {editingName ? (
                                <input 
                                    className="model-name-input" 
                                    value={model.name} 
                                    onChange={(e) => setModel(prev => ({ ...prev, name: e.target.value }))} 
                                />
                            ) : (
                                <div className="model-name-display">{model.name}</div>
                            )}
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Profile URL Slug</p>
                            </div>
                            <input
                                aria-label="Model slug"
                                className="model-name-input"
                                value={model.slug || ''}
                                onChange={(e) => setModel(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') }))}
                                maxLength="191"
                                placeholder="profile-slug"
                            />
                            <p className="model-desc">Lowercase letters, numbers, and hyphens are used in the public profile URL.</p>
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Status</p>
                            </div>
                            <select
                                aria-label="Model status"
                                className="model-status-select"
                                value={model.status || ''}
                                onChange={(e) => setModel(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="Active">Active</option>
                                <option value="Away">Away</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>

                        <div className="model-section campaign-editor-section">
                            <div className="section-title-row">
                                <p>Campaign</p>
                            </div>
                            <div className="campaign-editor-fields">
                                <label className="campaign-editor-field">
                                    <span>Campaign status</span>
                                    <select
                                        aria-label="Campaign status"
                                        value={model.campaign?.type === 'trial' ? 'active' : model.campaign?.status || 'inactive'}
                                        disabled={model.campaign?.type === 'trial'}
                                        onChange={(e) => setModel(prev => ({
                                            ...prev,
                                            campaign: { ...(prev.campaign || {}), type: 'paid', status: e.target.value }
                                        }))}
                                    >
                                        {campaignStatusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </label>
                                <label className="campaign-editor-field">
                                    <span>Campaign type</span>
                                    <select
                                        aria-label="Campaign type"
                                        value={model.campaign?.type === 'trial' ? 'trial' : model.campaign?.budget || 2}
                                        onChange={(e) => setModel(prev => {
                                            const value = e.target.value;
                                            if (value === 'trial') {
                                                return {
                                                    ...prev,
                                                    campaign: {
                                                        ...(prev.campaign || {}),
                                                        type: 'trial',
                                                        status: 'active',
                                                        budget: 0,
                                                        data: {
                                                            ...(prev.campaign?.data || {}),
                                                            type: 'trial',
                                                            trialDays: Number(prev.campaign?.data?.trialDays) || 7,
                                                            startsAt: prev.campaign?.data?.startsAt || defaultTrialStartsAt()
                                                        }
                                                    }
                                                };
                                            }
                                            return {
                                                ...prev,
                                                campaign: { ...(prev.campaign || {}), type: 'paid', status: ['auto', 'active', 'inactive'].includes(prev.campaign?.status) ? prev.campaign.status : 'inactive', budget: Number(value), data: {} }
                                            };
                                        })}
                                    >
                                        {campaignTypeOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </select>
                                </label>
                            </div>
                            {model.campaign?.type === 'trial' && (
                                <div className="campaign-trial-fields">
                                    <label className="campaign-editor-field">
                                        <span>Trial duration (days)</span>
                                        <input
                                            aria-label="Trial duration"
                                            type="number"
                                            min="1"
                                            max="365"
                                            value={model.campaign.data?.trialDays || 7}
                                            onChange={(e) => setModel(prev => ({
                                                ...prev,
                                                campaign: { ...prev.campaign, data: { ...prev.campaign.data, trialDays: e.target.value } }
                                            }))}
                                        />
                                    </label>
                                    <label className="campaign-editor-field">
                                        <span>Trial starts</span>
                                        <input
                                            aria-label="Trial starts"
                                            type="datetime-local"
                                            value={trialStartsAtInput(model.campaign.data?.startsAt)}
                                            onChange={(e) => setModel(prev => ({
                                                ...prev,
                                                campaign: { ...prev.campaign, data: { ...prev.campaign.data, startsAt: e.target.value } }
                                            }))}
                                        />
                                    </label>
                                    <p className="campaign-trial-note">Trial campaigns are created by administrators only, remain active, and use a zero budget.</p>
                                </div>
                            )}
                            {model.campaign?.expiresAt && <p className="campaign-expiry">Expires {new Date(model.campaign.expiresAt).toLocaleDateString()}</p>}
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Description</p>
                                <button className="section-btn" onClick={() => setEditingDesc(!editingDesc)}>
                                    {editingDesc ? 'Save' : 'Edit'}
                                </button>
                            </div>
                            {editingDesc ? (
                                <textarea 
                                    className="model-textarea" 
                                    value={model.description} 
                                    onChange={(e) => setModel(prev => ({ ...prev, description: e.target.value }))} 
                                />
                            ) : (
                                <p className="model-desc">{model.description}</p>
                            )}
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Contacts</p>
                                <button
                                    className="section-btn"
                                    aria-label={editingContacts ? 'Finish editing contacts' : 'Edit contacts'}
                                    onClick={() => setEditingContacts(!editingContacts)}
                                >
                                    {editingContacts ? 'Done' : 'Edit'}
                                </button>
                            </div>
                            <div className="model-contacts">
                                {Object.entries(model.contacts || {}).length === 0 && !editingContacts ? (
                                    <p className="contact-empty">No contacts added.</p>
                                ) : (
                                    Object.entries(model.contacts || {}).map(([key, value], index) => {
                                        const isCustom = Boolean(customContactTypes[key]) || !knownContactType(key);
                                        const selectedType = isCustom ? 'custom' : key;
                                        const contactOption = contactTypeOptions.find(option => option.value === selectedType);
                                        const displayLabel = isCustom
                                            ? (customContactLabels[key] || humanizeContactType(key))
                                            : contactOption.label;
                                        return editingContacts ? (
                                            <div className="contact-item contact-item-editing" key={key}>
                                                <div className="contact-field">
                                                    <label htmlFor={`contact-type-${index}`}>Type</label>
                                                    <select
                                                        id={`contact-type-${index}`}
                                                        aria-label={`Contact type ${index + 1}`}
                                                        value={selectedType}
                                                        onChange={(e) => updateContactType(key, e.target.value)}
                                                    >
                                                        {contactTypeOptions.map(option => (
                                                            <option
                                                                key={option.value}
                                                                value={option.value}
                                                                disabled={option.value !== 'custom' && option.value !== key && Object.prototype.hasOwnProperty.call(model.contacts, option.value)}
                                                            >
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {isCustom && (
                                                    <div className="contact-field">
                                                        <label htmlFor={`contact-name-${index}`}>Contact type name</label>
                                                        <input
                                                            id={`contact-name-${index}`}
                                                            type="text"
                                                            value={customContactLabels[key] ?? (knownContactType(key)?.label || '')}
                                                            onChange={(e) => updateCustomContactLabel(key, e.target.value)}
                                                            placeholder="e.g. Snapchat"
                                                        />
                                                    </div>
                                                )}
                                                <div className="contact-field contact-value-field">
                                                    <label htmlFor={`contact-value-${index}`}>{displayLabel || 'Custom'} value</label>
                                                    <input
                                                        id={`contact-value-${index}`}
                                                        aria-label={`${displayLabel || 'Custom'} value`}
                                                        type={contactOption?.inputType || 'text'}
                                                        value={value || ''}
                                                        onChange={(e) => updateContactValue(key, e.target.value)}
                                                        placeholder={contactOption?.placeholder || 'Contact value'}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="contact-remove"
                                                    aria-label={`Remove ${displayLabel || 'custom'} contact`}
                                                    onClick={() => removeContact(key)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="contact-item" key={key}>
                                                <span className="contact-label">{displayLabel}</span>
                                                <span className="contact-value">{value || '—'}</span>
                                            </div>
                                        );
                                    })
                                )}
                                {editingContacts && (
                                    <button type="button" className="section-btn contact-add" onClick={addContact}>
                                        Add Contact
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Model Parameters</p>
                                <button className="section-btn" onClick={() => setEditingParams(!editingParams)}>
                                    {editingParams ? 'Save' : 'Edit'}
                                </button>
                            </div>
                            <div className="model-params">
                                {Object.entries(model.parameters).map(([key, value]) => (
                                    <div className="param-item" key={key}>
                                        <span className="param-label">{key}</span>
                                        {editingParams ? (
                                            paramSelect(key) ? (
                                                <select 
                                                    className="param-input" 
                                                    value={value} 
                                                    onChange={(e) => handleParamChange(key, e.target.value)}
                                                >
                                                    {paramSelect(key).map(o => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            ) : (
                                                <input 
                                                    className="param-input" 
                                                    value={value} 
                                                    onChange={(e) => handleParamChange(key, e.target.value)} 
                                                />
                                            )
                                        ) : (
                                            <span className="param-value">{value}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="model-edit-right">
                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Location</p>
                            </div>
                            <div className="location-toggle">
                                <button 
                                    className={model.locationType === 'single' ? 'loc-btn active' : 'loc-btn'}
                                    onClick={() => setModel(prev => ({ ...prev, locationType: 'single' }))}
                                >
                                    Single City
                                </button>
                                <button 
                                    className={model.locationType === 'tour' ? 'loc-btn active' : 'loc-btn'}
                                    onClick={() => setModel(prev => ({ ...prev, locationType: 'tour' }))}
                                >
                                    Tour Schedule
                                </button>
                            </div>
                            {model.locationType === 'single' ? (
                                <div className="single-city">
                                    <select
                                        aria-label="Single city"
                                        value={model.singleCity} 
                                        onChange={(e) => setModel(prev => ({ ...prev, singleCity: e.target.value }))}
                                    >
                                        {optionsForValue(model.singleCity).map(city => <option key={city.value} value={city.value}>{city.label}</option>)}
                                    </select>
                                </div>
                            ) : (
                                <div className="tour-schedule">
                                    {model.tourSchedule.map((stop, i) => (
                                        <div className="tour-stop" key={i}>
                                            <select
                                                aria-label={`Tour stop city ${i + 1}`}
                                                value={stop.city}
                                                onChange={(e) => setModel(prev => ({
                                                    ...prev,
                                                    tourSchedule: prev.tourSchedule.map((s, idx) =>
                                                        idx === i ? { ...s, city: e.target.value } : s
                                                    )
                                                }))}
                                            >
                                                {optionsForValue(stop.city).map(city => <option key={city.value} value={city.value}>{city.label}</option>)}
                                            </select>
                                            <input 
                                                type="date" 
                                                value={stop.from} 
                                                onChange={(e) => setModel(prev => ({
                                                    ...prev,
                                                    tourSchedule: prev.tourSchedule.map((s, idx) => 
                                                        idx === i ? { ...s, from: e.target.value } : s
                                                    )
                                                }))} 
                                            />
                                            <input 
                                                type="date" 
                                                value={stop.to} 
                                                onChange={(e) => setModel(prev => ({
                                                    ...prev,
                                                    tourSchedule: prev.tourSchedule.map((s, idx) => 
                                                        idx === i ? { ...s, to: e.target.value } : s
                                                    )
                                                }))} 
                                            />
                                            <span 
                                                className="table-action delete" 
                                                onClick={() => askDelete('this tour stop', () => setModel(prev => ({
                                                    ...prev,
                                                    tourSchedule: prev.tourSchedule.filter((_, idx) => idx !== i)
                                                })))}
                                            >
                                                ×
                                            </span>
                                        </div>
                                    ))}
                                    <button 
                                        className="section-btn" 
                                        onClick={() => setModel(prev => ({
                                            ...prev,
                                            tourSchedule: [...prev.tourSchedule, { city: cityOptions[0]?.value || '', from: '', to: '' }]
                                        }))}
                                    >
                                        Add Stop
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Payment Methods</p>
                            </div>
                            {isLoadingPaymentMethods ? (
                                <p className="payment-methods-state" role="status">Loading payment methods...</p>
                            ) : paymentMethodsError ? (
                                <p className="payment-methods-state payment-methods-error" role="alert">{paymentMethodsError}</p>
                            ) : paymentMethods.length === 0 ? (
                                <p className="payment-methods-state">No payment methods on the linked account.</p>
                            ) : (
                                <div className="payment-method-list">
                                    {paymentMethods.map(method => (
                                        <div className="payment-method-item" key={method.uuid || `${method.type}-${method.last4}`}>
                                            <div className="payment-method-primary">
                                                <strong>{String(method.type || 'card').toUpperCase()}</strong>
                                                <span>•••• {method.last4 || '—'}</span>
                                            </div>
                                            <div className="payment-method-meta">
                                                <span>{method.expiry || 'No expiry'}</span>
                                                {method.default && <span className="payment-method-default">Default</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>City Rates</p>
                                <button className="section-btn" onClick={addCityRate}>Add Rate</button>
                            </div>
                            <table className="model-table">
                                <thead>
                                    <tr><th>Duration</th><th>Incall</th><th>Outcall</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {model.cityRates.map((rate, i) => (
                                        <tr key={i}>
                                            {editingCity === i ? (
                                                <>
                                                    <td>
                                                        <select 
                                                            value={rate.duration} 
                                                            onChange={(e) => handleCityRateChange(i, 'duration', e.target.value)}
                                                        >
                                                            <option value="">Select</option>
                                                            {durationOptions.map(d => <option key={d} value={d}>{d}</option>)}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input 
                                                            type="number" 
                                                            value={rate.incall || ''} 
                                                            onChange={(e) => handleCityRateChange(i, 'incall', e.target.value)} 
                                                        />
                                                    </td>
                                                    <td>
                                                        <input 
                                                            type="number" 
                                                            value={rate.outcall || ''} 
                                                            onChange={(e) => handleCityRateChange(i, 'outcall', e.target.value)} 
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className="table-action" onClick={() => setEditingCity(null)}>✓</span>
                                                        <span 
                                                            className="table-action delete" 
                                                            onClick={() => askDelete('this city rate', () => deleteCityRate(i))}
                                                        >
                                                            ×
                                                        </span>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>{rate.duration}</td>
                                                    <td>{rate.incall ? '€' + rate.incall : 'N/A'}</td>
                                                    <td>{rate.outcall ? '€' + rate.outcall : 'N/A'}</td>
                                                    <td>
                                                        <span className="table-action" onClick={() => setEditingCity(i)}>✎</span>
                                                        <span 
                                                            className="table-action delete" 
                                                            onClick={() => askDelete('this city rate', () => deleteCityRate(i))}
                                                        >
                                                            ×
                                                        </span>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="model-section">
                            <div className="section-title-row">
                                <p>Travel Rates</p>
                                <button className="section-btn" onClick={addTravelRate}>Add Rate</button>
                            </div>
                            <table className="model-table">
                                <thead>
                                    <tr><th>Duration</th><th>Incall</th><th>Outcall</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {model.travelRates.map((rate, i) => (
                                        <tr key={i}>
                                            {editingTravel === i ? (
                                                <>
                                                    <td>
                                                        <select 
                                                            value={rate.duration} 
                                                            onChange={(e) => handleTravelRateChange(i, 'duration', e.target.value)}
                                                        >
                                                            <option value="">Select</option>
                                                            {durationOptions.map(d => <option key={d} value={d}>{d}</option>)}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <input 
                                                            type="number" 
                                                            value={rate.incall || ''} 
                                                            onChange={(e) => handleTravelRateChange(i, 'incall', e.target.value)} 
                                                        />
                                                    </td>
                                                    <td>
                                                        <input 
                                                            type="number" 
                                                            value={rate.outcall || ''} 
                                                            onChange={(e) => handleTravelRateChange(i, 'outcall', e.target.value)} 
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className="table-action" onClick={() => setEditingTravel(null)}>✓</span>
                                                        <span 
                                                            className="table-action delete" 
                                                            onClick={() => askDelete('this travel rate', () => deleteTravelRate(i))}
                                                        >
                                                            ×
                                                        </span>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>{rate.duration}</td>
                                                    <td>{rate.incall ? '€' + rate.incall : 'N/A'}</td>
                                                    <td>{rate.outcall ? '€' + rate.outcall : 'N/A'}</td>
                                                    <td>
                                                        <span className="table-action" onClick={() => setEditingTravel(i)}>✎</span>
                                                        <span 
                                                            className="table-action delete" 
                                                            onClick={() => askDelete('this travel rate', () => deleteTravelRate(i))}
                                                        >
                                                            ×
                                                        </span>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>

                    </div>
                    </fieldset>
                )}
            </div>

            {lightbox && (
                <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
                    <span className="lightbox-close">&times;</span>
                    <img src={lightbox} alt="" className="lightbox-img" />
                </div>
            )}

            {confirmDelete && (
                <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Are you sure?</h3>
                            <button className="modal-close" onClick={() => setConfirmDelete(null)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete {confirmDelete.label}? This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={() => setConfirmDelete(null)}>
                                Cancel
                            </button>
                            <button className="modal-button delete" onClick={handleConfirmDelete} style={{ background: '#d1242f' }}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showUpload && (
                <div className="modal-overlay" onClick={closePhotoUpload}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Upload Photo</h3>
                            <button className="modal-close" onClick={closePhotoUpload} aria-label="Close photo upload">
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p className="upload-desc">Drop an image here or browse your files. Photos upload securely to S3 and are added to the model draft; click Save Changes afterward to persist the model.</p>
                            <div
                                className={`photo-dropzone${isDraggingPhoto ? ' is-dragging' : ''}`}
                                role="button"
                                tabIndex="0"
                                aria-label="Drop photo here or browse files"
                                onClick={() => photoFileInput.current?.click()}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        photoFileInput.current?.click();
                                    }
                                }}
                                onDragEnter={(event) => {
                                    event.preventDefault();
                                    setIsDraggingPhoto(true);
                                }}
                                onDragOver={(event) => event.preventDefault()}
                                onDragLeave={() => setIsDraggingPhoto(false)}
                                onDrop={handlePhotoDrop}
                            >
                                <input
                                    ref={photoFileInput}
                                    className="photo-upload-input"
                                    id="photo-file"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                                    aria-label="Choose photo file"
                                    onChange={handlePhotoFileChange}
                                    onClick={(event) => event.stopPropagation()}
                                    disabled={isUploadingPhoto}
                                />
                                <strong>{photoFile ? photoFile.name : 'Drop photo here or browse files'}</strong>
                                <span>JPEG, PNG, WebP, GIF - up to 10 MB</span>
                            </div>
                            {photoError && <p className="error-msg" role="alert">{photoError}</p>}
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={closePhotoUpload} disabled={isUploadingPhoto}>
                                Cancel
                            </button>
                            <button className="modal-button create" onClick={addPhoto} disabled={isUploadingPhoto || !photoFile}>
                                {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelEdit;